import { useState } from 'react'
import { ExternalLink, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useConnect, useConnections, useDeleteConnection, useSyncConnection } from '@/hooks/useIntegrations'
import type { ExternalConnection } from '@/api/integrations'
import { SERVICE_CATALOG, type ServiceCatalogEntry } from '@/lib/service-catalog'

type Props = {
  open: boolean
  onClose: () => void
}

function detailMessage(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail
  return detail ?? fallback
}

function formatSynced(value: string | null): string {
  if (!value) return 'まだ同期していません'
  const d = new Date(value)
  return `最終同期: ${d.toLocaleString('ja-JP', { dateStyle: 'short', timeStyle: 'short' })}`
}

function ServiceRow({
  service,
  connection,
}: {
  service: ServiceCatalogEntry
  connection?: ExternalConnection
}) {
  const sync = useSyncConnection()
  const connect = useConnect()
  const remove = useDeleteConnection()
  const [connecting, setConnecting] = useState(false)
  const [token, setToken] = useState('')

  const handleSync = async () => {
    try {
      const result = await sync.mutateAsync(connection!.id)
      toast.success(`${service.label} から ${result.created} 件取り込みました（更新 ${result.updated} 件）`)
    } catch (e) {
      toast.error(detailMessage(e, '同期に失敗しました'))
    }
  }

  const handleConnect = async () => {
    const trimmed = token.trim()
    if (!trimmed) return
    try {
      await connect.mutateAsync({ provider: service.id, token: trimmed })
      toast.success(`${service.label} と連携しました`)
      setConnecting(false)
      setToken('')
    } catch (e) {
      toast.error(detailMessage(e, '連携に失敗しました'))
    }
  }

  const handleRemove = async () => {
    try {
      await remove.mutateAsync(connection!.id)
      toast.success(`${service.label} の連携を解除しました`)
    } catch {
      toast.error('連携解除に失敗しました')
    }
  }

  return (
    <div className="rounded-xl border p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium flex items-center gap-2">
            {service.label}
            {connection && (
              <span className="text-[10px] rounded-full bg-primary/10 text-primary px-2 py-0.5">
                連携済み
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {connection ? formatSynced(connection.last_synced_at) : service.description}
          </p>
        </div>

        {connection ? (
          <div className="flex items-center gap-1 shrink-0">
            <Button className="min-h-[44px]" size="sm" onClick={handleSync} disabled={sync.isPending}>
              {sync.isPending && <Loader2 size={14} className="animate-spin mr-1" />}
              同期
            </Button>
            <Button
              className="min-h-[44px]"
              size="sm"
              variant="ghost"
              onClick={handleRemove}
              disabled={remove.isPending}
            >
              解除
            </Button>
          </div>
        ) : (
          !connecting && (
            <Button
              className="min-h-[44px] shrink-0"
              size="sm"
              variant="outline"
              onClick={() => setConnecting(true)}
            >
              連携する
            </Button>
          )
        )}
      </div>

      {connecting && !connection && (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-muted-foreground">
            {service.label} でトークンを発行し、貼り付けてください。
          </p>
          {service.tokenPageUrl ? (
            <a href={service.tokenPageUrl} target="_blank" rel="noopener noreferrer">
              <Button className="min-h-[44px] w-full gap-1.5" size="sm" variant="outline">
                トークン発行ページを開く
                <ExternalLink size={14} />
              </Button>
            </a>
          ) : (
            <p className="text-xs text-destructive">発行ページ URL が未設定です。</p>
          )}
          <Input
            type="password"
            autoComplete="off"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="連携トークンを貼り付け"
          />
          <div className="flex gap-2">
            <Button
              className="min-h-[44px] flex-1"
              size="sm"
              onClick={handleConnect}
              disabled={!token.trim() || connect.isPending}
            >
              {connect.isPending && <Loader2 size={14} className="animate-spin mr-1" />}
              連携する
            </Button>
            <Button
              className="min-h-[44px]"
              size="sm"
              variant="ghost"
              onClick={() => {
                setConnecting(false)
                setToken('')
              }}
            >
              キャンセル
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function ExternalImportSheet({ open, onClose }: Props) {
  const { data: connections = [], isLoading } = useConnections()
  const byProvider = new Map(connections.map((c) => [c.provider, c]))

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>外部取り込み</SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          {isLoading ? (
            <div className="flex justify-center py-8 text-muted-foreground">
              <Loader2 className="animate-spin" />
            </div>
          ) : SERVICE_CATALOG.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">
              連携できるサービスがありません
            </p>
          ) : (
            SERVICE_CATALOG.map((service) => (
              <ServiceRow
                key={service.id}
                service={service}
                connection={byProvider.get(service.id)}
              />
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
